import { Controller } from '@hotwired/stimulus';

const MINUTES_IN_DAY = 24 * 60;

const minutesOf = (time) => {
  const [hours, minutes] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
};

// What a part makes of the field(s) it watches. Pure functions of the values and the part's own
// data, so the controller below stays "keep these spans in step with those fields" and none of
// this needs a rendered page to be reasoned about.
const FORMATS = {
  // Already verified data has no editable field in the row. Keep the server-rendered value
  // stable when the controller connects instead of treating the missing picker as an empty
  // answer.
  static: (_, { text }) => text,

  // A control that is either set or not: what it is worth saying is the caller's word for it,
  // not the value the input happens to carry (a headliner radio carries a row id).
  flag: ([set], { text }) => (set ? text : ''),

  range: ([from, to]) => (from && to ? `${from} - ${to}` : from),

  // Wrapping past midnight the way a night does rather than the way a clock does: a set that
  // ends at 00:15 having started at 23:30 is forty-five minutes, not minus a day. Slot's
  // NIGHT_START says the same thing server-side.
  duration: ([from, to], { unitHours, unitMinutes }) => {
    if (!from || !to) return '';

    const total = (minutesOf(to) - minutesOf(from) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
    const hours = Math.floor(total / 60);
    const minutes = total % 60;

    return [hours && `${hours}${unitHours}`, minutes && `${minutes}${unitMinutes}`]
      .filter(Boolean)
      .join(' ');
  },
};

// Keeps a repeating row's heading saying what is in the row, as it is typed. Which fields go
// into that heading is the caller's choice, made in RowCardComponent - a slot says its band
// and its hours, a gear item its name, count and category, a price list row its service and
// price.
//
// Goes on the ROW, not on the heading: the fields are siblings of the heading, so their events
// reach here on the way up and never reach the heading itself.
//
//   <div data-controller="row-heading" data-action="input->row-heading#sync change->row-heading#sync"
//        data-row-heading-placeholder-value="wolny slot">
//     <span data-row-heading-target="placeholder">wolny slot</span>
//     <span data-row-heading-target="part" data-source='[name$="[name]"]'></span>
//     ...the fields...
//   </div>
//
// Progressive enhancement: the component renders each part with its value already in it, so
// with the bundle blocked the heading is right and simply stops following the fields.
export default class extends Controller {
  static targets = ['part', 'placeholder'];
  static values = { placeholder: String };

  connect() {
    this.onGroupChange = this.onGroupChange.bind(this);
    // A `flag` part watches a radio group, and a radio group spans the whole editor: checking
    // one silently unchecks another, and the browser fires NOTHING on the input it unchecked.
    // The row that lost the flag would go on claiming it - two headliners on one bill - so it
    // has to hear about changes made outside itself.
    //
    // Which is why the listener sits on the form and answers only to that group: every row
    // registers one, and re-rendering all of them on every keystroke in the editor would be a
    // lot of nothing.
    const flag = this.partTargets.find((part) => part.dataset.format === 'flag');
    this.group = flag && flag.dataset.source.replace(':checked', '');
    if (this.group) {
      this.watched = this.element.closest('form') || document;
      this.watched.addEventListener('change', this.onGroupChange);
    }

    this.sync();
  }

  disconnect() {
    if (this.watched) this.watched.removeEventListener('change', this.onGroupChange);
  }

  onGroupChange(event) {
    if (event.target.matches(this.group)) this.sync();
  }

  sync() {
    const said = this.partTargets.map((part) => this.write(part));

    // Nothing said yet - a row waiting to be filled in, which should say so rather than be a
    // number with a blank after it.
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.textContent = said.some(Boolean) ? '' : this.placeholderValue;
    }
  }

  // Empty rather than absent: `:empty` in the CSS is what takes a part, and the gap beside it,
  // out of the line, and the span has to stay for the next keystroke to write into.
  write(part) {
    const value = this.valueFor(part);
    part.textContent = value ? `${part.dataset.prefix || ''}${value}` : '';
    return value;
  }

  valueFor(part) {
    const { source, until, format, blankWhen } = part.dataset;
    const values = [source, until].map((selector) => this.fieldValue(selector));

    if (format) return FORMATS[format](values, part.dataset) || '';

    return values[0] === blankWhen ? '' : values[0];
  }

  fieldValue(selector) {
    const field = selector && this.element.querySelector(selector);
    if (!field) return '';

    // A picker's answer is the label of the radio it keeps checked, not the label its trigger is
    // showing: SelectComponent fires its `change` BEFORE relabelling the trigger, so reading the
    // screen gives the pick before last while the checked input is already right.
    return (field.dataset.selectLabel ?? field.value).trim();
  }
}
